/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {isPositiveInteger} from '@ion/core/src/utils/Validators';
import useFormFieldValidation from '../../hooks/useFormFieldValidation';
import {Props} from '../textfield/TextField';
import {FormNumberField} from './FormNumberField';

export interface FormIntegerFieldProps extends Props {
  fieldHasValidationError?: (hasError: boolean) => void;
}

/**
 * The FormIntegerField component is a re-usable component designed for any user input
 * that requires a whole, positive, integer.
 *
 * Zero is considered a valid input.
 * The field is limited to 10 characters, which is the length of an integer.
 *
 * ```typescript
 *
 *  import {FormIntegerField} from '@ion/src/components/forms';
 *
 *  <FormIntegerField fieldLabel={label}
 *    placeholder={placeHolderText}
 *    errorText={errorText}
 *    hasError={hasError}
 *    defaultValue={value}
 *    onChange={onChange}
 *    name={name}
 *    fieldHasValidationError={notifyParentComponentOfFieldValidationError}
 *    disabled={disabled} />
 *
 * ```
 */
export const FormIntegerField = ({
  hasError,
  errorText,
  defaultValue,
  maxLength,
  fieldHasValidationError,
  ...props
}: FormIntegerFieldProps) => {
  const [hasValidationError, errorMessage] = useFormFieldValidation({
    validate          : () => isEmpty(defaultValue as string) || isPositiveInteger(defaultValue.toString() as string),
    hasFormError      : hasError,
    formErrorMessage  : errorText as string,
    fieldErrorMessage : translate('common:validation.errorMessages.positiveInteger')
  });

  useEffect((): void => {
    fieldHasValidationError?.(hasValidationError);
  }, [hasValidationError]);

  return (
    <FormNumberField isNumericString
      defaultValue={defaultValue}
      hasError={hasValidationError}
      errorText={errorMessage}
      thousandSeparator={false}
      maxLength={maxLength}
      {...props} />
  );
};

export default FormIntegerField;
